import adminSocketService from './socketService';

class WebRTCManager {
    peerConnections = new Map();
    streams = new Map();
    streamListeners = new Map();
    candidateQueue = new Map();
    initialized = false;

    initialize() {
        if (this.initialized) return;
        const socket = adminSocketService.socket;
        if (!socket) {
            setTimeout(() => this.initialize(), 1000);
            return;
        }
        this.initialized = true;
        socket.on('webrtc-offer', this.handleOffer);
        socket.on('webrtc-ice-candidate', this.handleCandidate);
        socket.on('webrtc-error', this.handleError);
        socket.on('device-list', this.handleDeviceList);
    }

    handleDeviceList = (devices) => {
        const socket = adminSocketService.socket;
        if (!socket) return;

        const validDevices = devices.filter(d => d.socketId);

        validDevices.forEach((device) => {
            const pc = this.peerConnections.get(device.socketId);
            if (!pc || (pc !== null && pc.connectionState === 'closed')) {
                if (pc) this.closeConnection(device.socketId);
                console.log(`Auto-requesting stream for ${device.socketId}`);
                socket.emit('request-stream', { targetSocketId: device.socketId, adminId: socket.id });
                this.peerConnections.set(device.socketId, null);
            }
        });
        const onlineSocketIds = new Set(validDevices.map(d => d.socketId));
        for (const socketId of this.peerConnections.keys()) {
            if (!onlineSocketIds.has(socketId)) {
                this.closeConnection(socketId);
            }
        }
    };

    handleOffer = async ({ offer, from }) => {
        const socket = adminSocketService.socket;
        if (!socket) return;
        let pc = this.peerConnections.get(from);
        if (!pc || pc === null) {
            pc = new RTCPeerConnection({
                iceServers: [
                    { urls: 'stun:stun.cloudflare.com:3478' },
                    { urls: 'stun:stun.l.google.com:19302' },
                    { urls: 'stun:stun1.l.google.com:19302' },
                    {
                        urls: 'turn:agentapi.dynamicsoftlink.com:3478?transport=tcp',
                        username: 'admin',
                        credential: 'dynamicpassword123'
                    },
                    {
                        urls: 'turn:agentapi.dynamicsoftlink.com:3478?transport=udp',
                        username: 'admin',
                        credential: 'dynamicpassword123'
                    }
                ]
            });
            pc.ontrack = (event) => {
                console.log(`Received track from ${from}`);
                this.streams.set(from, event.streams[0]);
                this.notifyListeners(from, event.streams[0]);
            };
            pc.onicecandidate = (event) => {
                if (event.candidate) {
                    socket.emit('webrtc-ice-candidate', {
                        targetSocketId: from,
                        candidate: event.candidate,
                        from: socket.id
                    });
                }
            };
            pc.oniceconnectionstatechange = () => {
                console.log(`[ICE State Admin] ${from}: ${pc.iceConnectionState}`);
                if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'closed') {
                    console.error(`[ICE State Admin] ICE Connection Failed/Closed for ${from}. Cleaning up...`);
                    this.closeConnection(from);
                }
            };
            this.peerConnections.set(from, pc);
        }
        try {
            console.log(`[Admin WebRTC] Received offer from ${from}. Setting remote description...`);
            await pc.setRemoteDescription(new RTCSessionDescription(offer));
            console.log(`[Admin WebRTC] Remote description set successfully for ${from}`);

            if (this.candidateQueue.has(from)) {
                const candidates = this.candidateQueue.get(from);
                for (const c of candidates) {
                    pc.addIceCandidate(new RTCIceCandidate(c)).catch(err => console.error('Error adding queued ICE candidate', err));
                }
                this.candidateQueue.delete(from);
            }

            console.log(`[Admin WebRTC] Creating answer for ${from}...`);
            const answer = await pc.createAnswer();
            console.log(`[Admin WebRTC] Answer created successfully. Setting local description...`);
            await pc.setLocalDescription(answer);
            console.log(`[Admin WebRTC] Local description set. Emitting webrtc-answer to ${from}`);
            socket.emit('webrtc-answer', {
                targetSocketId: from,
                answer,
                from: socket.id
            });
        }
        catch (err) {
            console.error('[Admin WebRTC] Error handling offer from', from, err);
        }
    };

    handleCandidate = async ({ candidate, from }) => {
        const pc = this.peerConnections.get(from);

        if (!pc || !pc.remoteDescription) {
            if (!this.candidateQueue.has(from)) {
                this.candidateQueue.set(from, []);
            }
            this.candidateQueue.get(from).push(candidate);
            return;
        }

        if (pc) {
            try {
                await pc.addIceCandidate(new RTCIceCandidate(candidate));
            }
            catch (err) {
                console.error('Error adding ICE candidate', err);
            }
        }
    };

    handleError = ({ error, from }) => {
        if (from) {
            this.notifyListeners(from, null, error);
        }
    };

    subscribe(targetSocketId, callback) {
        if (!this.streamListeners.has(targetSocketId)) {
            this.streamListeners.set(targetSocketId, new Set());
        }
        this.streamListeners.get(targetSocketId).add(callback);
        if (this.streams.has(targetSocketId)) {
            callback(this.streams.get(targetSocketId));
        } else {
            const socket = adminSocketService.socket;
            const pc = this.peerConnections.get(targetSocketId);
            if (socket && socket.connected && (!pc || pc === null || pc.connectionState === 'closed')) {
                console.log(`[WebRTCManager] Requesting stream on subscribe for ${targetSocketId}`);
                socket.emit('request-stream', { targetSocketId, adminId: socket.id });
                this.peerConnections.set(targetSocketId, null);
            }
        }
        return () => {
            this.streamListeners.get(targetSocketId)?.delete(callback);
        };
    }

    notifyListeners(targetSocketId, stream, error) {
        const listeners = this.streamListeners.get(targetSocketId);
        if (listeners) {
            listeners.forEach(cb => cb(stream, error));
        }
    }

    closeConnection(targetSocketId) {
        const pc = this.peerConnections.get(targetSocketId);
        if (pc) {
            pc.close();
        }
        this.peerConnections.delete(targetSocketId);
        this.streams.delete(targetSocketId);
        this.candidateQueue.delete(targetSocketId);
        this.notifyListeners(targetSocketId, null);
    }
}

export const webrtcManager = new WebRTCManager();
